import { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown, LayoutGrid, MessageCircle, PhoneOff } from 'lucide-react';
import {
  ManifestAvatarHomeEmbed,
  ensureManifestHostTools,
  startAvatarConversation,
  endAvatarConversation,
  AVATAR_CONVERSATION_STATE_EVENT,
  type AvatarConversationUiState,
} from './ManifestAvatarEmbed';
import AvatarPromptChips from './AvatarPromptChips';
import AvatarSeoHidden from './AvatarSeoHidden';
import AvatarStaticSplitPanel from './AvatarStaticSplitPanel';
import { openSplitScreenForSection } from '../lib/avatarActions';
import { installAvatarHomeNavigationGuard } from '../lib/avatarHomeNavigationGuard';
import { installManifestHomeSplitSync } from '../lib/manifestAvatarBridge';

interface AvatarHomeHeroProps {
  onContactClick?: () => void;
  onPartnerClick?: () => void;
}

export default function AvatarHomeHero(_props: AvatarHomeHeroProps) {
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [avatarState, setAvatarState] = useState<AvatarConversationUiState>({
    conversationStarted: false,
    status: 'idle',
    sessionEnded: false,
  });

  useEffect(() => {
    installAvatarHomeNavigationGuard();
    installManifestHomeSplitSync();
    ensureManifestHostTools()
      .then(() => installManifestHomeSplitSync())
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const onState = (event: Event) => {
      const detail = (event as CustomEvent<AvatarConversationUiState>).detail;
      if (!detail) return;
      setAvatarState(detail);
    };
    window.addEventListener(AVATAR_CONVERSATION_STATE_EVENT, onState);
    return () => window.removeEventListener(AVATAR_CONVERSATION_STATE_EVENT, onState);
  }, []);

  const isLive = avatarState.conversationStarted && avatarState.status === 'ready';
  const isLoading = avatarState.conversationStarted && avatarState.status === 'loading';
  const talkLabel = isLive
    ? 'End call'
    : avatarState.sessionEnded
      ? 'Start new conversation'
      : isLoading
        ? 'Connecting…'
        : 'Talk';

  const handleTalkClick = () => {
    if (isLoading) return;
    if (isLive) {
      endAvatarConversation();
      return;
    }
    startAvatarConversation();
  };

  return (
    <>
      <AvatarSeoHidden />
      <section
        id="avatar-hero"
        data-avatar-target="living-websites"
        className={`avatar-hero-split-static fixed inset-0 z-10 overflow-hidden bg-black${
          isLive || isLoading ? ' avatar-hero-split-static--live' : ''
        }`}
      >
        <div className="avatar-hero-split-static__avatar relative min-h-0 flex-1">
          <div className="avatar-embed-frame absolute inset-0">
            <ManifestAvatarHomeEmbed />
          </div>

          <div className="avatar-hero-chrome">
            {topicsOpen && (
              <div className="avatar-hero-chrome__chips">
                <AvatarPromptChips
                  compact
                  className="flex-nowrap overflow-x-auto scrollbar-none"
                />
              </div>
            )}

            <div className="avatar-hero-chrome__row">
              <button
                type="button"
                onClick={() => setTopicsOpen((open) => !open)}
                className="avatar-hero-chrome__btn avatar-hero-chrome__btn--topics"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                {topicsOpen ? 'Hide topics' : 'Explore topics'}
                {topicsOpen ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronUp className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={handleTalkClick}
                disabled={isLoading}
                className={`avatar-hero-chrome__btn avatar-hero-chrome__btn--talk${
                  isLive ? ' avatar-hero-chrome__btn--talk-live' : ''
                }`}
              >
                {isLive ? (
                  <PhoneOff className="w-3.5 h-3.5" />
                ) : (
                  <MessageCircle className="w-3.5 h-3.5" />
                )}
                {talkLabel}
              </button>
              <button
                type="button"
                onClick={() => openSplitScreenForSection('products')}
                className="avatar-hero-chrome__btn avatar-hero-chrome__btn--browse"
              >
                Browse site
              </button>
            </div>
          </div>
        </div>

        <AvatarStaticSplitPanel />
      </section>
    </>
  );
}
